import { create } from 'zustand';
import { UserResponse } from '../types';
import { authService } from '../services/auth';

interface AuthState {
  user: UserResponse | null;
  isAuthenticated: boolean;
  loading: boolean;
  isTester: () => boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string, name?: string) => Promise<void>;
  logout: () => void;
  loadUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: authService.isAuthenticated(),
  loading: false,

  isTester: () => {
    const user = get().user;
    return user?.is_tester || user?.roles?.includes('tester') || user?.roles?.includes('admin') || false;
  },

  login: async (username: string, password: string) => {
    set({ loading: true });
    try {
      const token = await authService.login(username, password);
      console.log('Login successful, token received:', token ? 'Yes' : 'No');
      
      // Token alındı, hemen isAuthenticated'i true yap
      // Böylece navigate çalıştığında state güncel olur
      set({ isAuthenticated: true });
      
      // Token alındı, şimdi kullanıcı bilgisini al (timeout ile)
      try {
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Add timeout to prevent infinite loading
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error('getCurrentUser timeout'));
          }, 5000); // 5 second timeout
        });
        
        const user = await Promise.race([
          authService.getCurrentUser(),
          timeoutPromise
        ]) as any;
        
        console.log('User info received:', user);
        set({ user, loading: false });
      } catch (userError: any) {
        console.warn('getCurrentUser failed, but login was successful:', userError);
        // Token var, kullanıcı bilgisi alınamasa bile login başarılı sayılabilir
        // Basit bir user objesi oluştur
        set({ 
          user: { username, email: '', name: username }, 
          loading: false 
        });
      }
    } catch (error: any) {
      console.error('Login error in store:', error);
      set({ loading: false, isAuthenticated: false });
      throw error;
    }
  },

  register: async (username: string, email: string, password: string, name?: string) => {
    set({ loading: true });
    try {
      await authService.register({ username, email, password, name });
      set({ loading: false });
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },

  logout: () => {
    authService.logout();
    set({ user: null, isAuthenticated: false });
  },

  loadUser: async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      set({ isAuthenticated: false, user: null, loading: false });
      return;
    }
    
    set({ loading: true });
    
    // Add timeout to prevent infinite loading
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('User loading timeout'));
      }, 5000); // 5 second timeout
    });
    
    try {
      const user = await Promise.race([
        authService.getCurrentUser(),
        timeoutPromise
      ]) as any;
      
      console.log('User loaded successfully:', user);
      console.log('User roles:', user.roles);
      console.log('Is admin?', user.roles?.includes('admin'));
      set({ user, isAuthenticated: true, loading: false });
    } catch (error: any) {
      console.error('Failed to load user:', error);
      set({ loading: false }); // Always set loading to false
      
      // Timeout veya network hatası
      if (error?.message === 'User loading timeout' || error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK') {
        console.warn('User loading timeout or network error, clearing token');
        localStorage.removeItem('access_token');
        set({ isAuthenticated: false, user: null });
      }
      // Token geçersizse veya kullanıcı bulunamazsa temizle
      else if (error?.response?.status === 401 || error?.response?.status === 404) {
        localStorage.removeItem('access_token');
        set({ isAuthenticated: false, user: null });
      } else {
        // Diğer hatalarda token varsa authenticated say ama user null bırak
        set({ isAuthenticated: true, user: null });
      }
    }
  },
}));

